<script setup>
import AddonCheckbox from "@/components/AddonCheckbox.vue";
import {computed, ref} from "vue";
import {useApartmentStore} from "@/stores/apartment";
import {useBookingStore} from "@/stores/booking";
import {useGuestsStore} from "@/stores/guests";
import {useI18n} from "vue-i18n";
import {useToast} from "vue-toastification";
import {useRouter} from "vue-router";
import Modal from "@/components/Modal.vue";
import AppIcon from "@/components/AppIcon.vue";
import BillingSection from "@/components/BillingSection.vue";

const apartmentStore = useApartmentStore()
const bookingStore = useBookingStore()
const guestsStore = useGuestsStore()
const i18n = useI18n()
const toast = useToast()
const router = useRouter()

const isCheckingIn = ref(false)
const bmgHint = ref(false)
const consent = ref(false)

const hasApartmentAddons = computed(() => {
  const addons = apartmentStore.apartment.availableAddons
  for (const key in addons) {
    if (addons[key] > 0) {
      return true
    }
  }

  return false
})

const guestsComplete = computed(() => {
  return guestsStore.guestsValidAmount === bookingStore.booking.guests.totalAmount
})

// Die Rechnungsanschrift ist freiwillig (Entscheidung Rida, 07.10.2026): nur
// wer eine Rechnung auf seinen Namen oder seine Firma braucht, gibt sie an —
// dann mit E-Mail-Adresse, sonst käme die Rechnung nicht an. Wer keine
// braucht, checkt ohne ein; die Rechnung läuft dann wie bis August auf den
// Sammelkunden, und in sevDesk entsteht nicht für jeden Gast ein eigener Kunde.
const canCheckIn = computed(() => guestsComplete.value)

const isReady = computed(() => canCheckIn.value && consent.value)

// Was genau noch fehlt — sonst starrt der Gast auf einen grauen Button.
const missingHint = computed(() => {
  if (!guestsComplete.value) {
    return i18n.t('checkinPage.missingGuests')
  }

  if (!consent.value) {
    return i18n.t('checkinPage.missingConsent')
  }

  return ''
})

function checkIn() {
  if (!isReady.value) {
    return
  }

  isCheckingIn.value = true

  guestsStore.checkIn()
      .then(() => router.push({name: 'booking.checkin.done'}))
      .catch(err => {
        console.error(err)
        toast.error(i18n.t('alerts.somethingWentWrong'));
      })
      .finally(() => isCheckingIn.value = false)
}
</script>

<template>
  <!-- Eigenes <form>, deshalb NEBEN dem Check-in-Formular statt darin:
       verschachtelte Formulare wirft der Parser weg, und der Speichern-Button
       der Anschrift würde den Check-in absenden. -->
  <billing-section/>

  <form action="#" @submit.prevent="checkIn" class="checkin-form">
    <template v-if="hasApartmentAddons">
      <h2 class="ww-eyebrow checkin-form__addons-label">{{ $t('addons') }}</h2>
      <fieldset class="ww-card checkin-form__addons">
        <template v-for="(value, addonName) in apartmentStore.apartment.availableAddons" :key="addonName">
          <addon-checkbox
              :title="'addonTypes.' + addonName"
              :available="value"
              v-model="bookingStore.booking.addons[addonName]"
              v-if="value > 0"
          />
        </template>
      </fieldset>
    </template>

    <label class="ww-card checkin-form__consent" for="signature">
      <input
          type="checkbox"
          class="ww-checkbox"
          id="signature"
          name="signature"
          v-model="consent"
      >
      <span class="checkin-form__consent-text">
        <span v-html="$t('signatureAgreement')"></span>
        {{ $t('moreInfo') }}
        <a href="#" @click.prevent.stop="bmgHint = true">{{ $t('here') }}</a>
      </span>
    </label>

    <button
        type="submit"
        class="ww-btn ww-btn--primary checkin-form__submit"
        :disabled="!isReady || isCheckingIn"
    >
      <span v-if="isCheckingIn" class="ww-spinner"></span>
      <app-icon v-else name="how_to_reg" :size="21"/>
      {{ $t('checkinNow') }}
    </button>

    <p v-if="missingHint" class="checkin-form__missing">
      <app-icon name="info" :size="17"/>
      {{ missingHint }}
    </p>
  </form>

  <modal v-model:show="bmgHint" :title="$t('bmgModal.title')">
    <template #content>
      <p v-html="$t('bmgModal.content')"></p>
    </template>
  </modal>
</template>

<style scoped>
.checkin-form {
  display: block;
}

.checkin-form__addons-label {
  margin-top: 26px;
}

.checkin-form__addons {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  margin: 12px 0 0;
  border: 1px solid var(--ww-border);
  padding: 8px 18px;
}

.checkin-form__consent {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  margin-top: 20px;
  padding: 16px;
  border-radius: var(--ww-r-lg);
  cursor: pointer;
}

.checkin-form__consent-text {
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--ww-text-body);
}

.checkin-form__submit {
  margin-top: 14px;
  padding: 18px;
  font-size: 17px;
}

.checkin-form__missing {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 11px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--ww-text-muted);
}

.checkin-form__missing :deep(.ww-icon) {
  color: var(--ww-icon-muted);
}
</style>
